import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';

import {
  BarChartConfig,
  ChartTypeEnum,
  DonutChartConfig,
  MostLeastEnum,
  TableConfig,
  TimelineChartConfig,
} from '../../../shared/models/chart.models';
import { InsSelectionType, TableRenderEnum } from '@nx-fe/table';
import {
  WEB_APP_SPOTLIGHT_INFO_PAGE,
  WEB_APP_SPOTLIGHT_LIST_PAGE,
  CHART_UI_BAR_TOPN,
  CHART_UI_BAR_TOPN_MANY,
  CHART_UI_DONUT_TOPN,
  DEFAULT_APP_FILTER_BAR_CONFIG,
  DEVICE_APP,
  DEVICE_LIST,
  DEFAULT_WEBAPP_FILTER_BAR_CONFIG,
  CHART_UI_DONUT_LEAST_MOST,
} from './app-mapper.constants';

import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { PageCategory } from '../../../shared/features/ng-redux/page/page.models';
import { ColorConstants } from '../../../../../../xs-colors';
import { ProfilePermissionEnum } from '@nx-fe/services';
import { CONSTANTS } from '../../../shared/localizations/dictionary';

export function mapWebAppSpotlightConfig(app: ApplicationConfigWithPages): ApplicationConfigWithPages {
  app.group = 'device_filter_app_group';
  app.name = 'web_as_name';
  app.icon = 'insight-icon-web-app-list';
  app.claims = [ProfilePermissionEnum.ProfileViewWebAppUsageDashboard];
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  patchPage(app, WEB_APP_SPOTLIGHT_LIST_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_WEBAPP_FILTER_BAR_CONFIG;
    page.pageCategory = PageCategory.WEBAPP;
    page.shiftEnabled = false;
    page.canManageApps = true;
    page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

    patchChart(page.charts, 'web-as-appTable', (chart: TableConfig) => {
      chart.name = 'label_Web_Applications';
      chart.fullName = 'as_table_app_fullname';

      chart.filters = {
        sortAttribute: 'webAppName',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'webAppName',
          label: 'webapp_table_app_column_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
            eventParams: {
              surfApp: true,
            },
          },
        },
        {
          id: 'category',
          label: 'webapp_category_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          renderer: TableRenderEnum.NUMBER,
          label: 'webapp_visit_label',
        },
        {
          id: 'consumedTime',
          renderer: TableRenderEnum.TIME,
          label: 'foreground_label',
        },
        {
          id: 'clientErrors',
          label: 'client_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'serverErrors',
          label: 'server_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'webViewErrors',
          label: 'webview_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'sslErrors',
          label: 'ssl_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  patchPage(app, WEB_APP_SPOTLIGHT_INFO_PAGE, page => {
    page.claims = [ProfilePermissionEnum.ProfileViewWebAppUsageDashboard];
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_APP_FILTER_BAR_CONFIG;
    page.pageCategory = PageCategory.WEBAPP;
    page.shiftEnabled = false;
    page.canManageApps = true;
    page.childMcGroups = true;
    page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

    page.predefinedCharts = [
      {
        category: {
          name: 'as_category_device',
          icon: 'insight-icon-device',
          tooltip: 'aps_category_device_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'web-as-manufacturer', 'as_chart_predefined_manufacturer_name'),
          buildChartOptions(page.charts, 'web-as-model', 'as_chart_predefined_model_name'),
          buildChartOptions(page.charts, 'web-as-osVersion', 'as_chart_predefined_osVersion_name'),
        ],
      },
      {
        category: {
          name: 'as_category_application',
          icon: '',
          tooltip: 'aps_category_app_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'web-as-deviceStats', 'web_as_chart_deviceStats_name'),
          buildChartOptions(page.charts, 'web-as-foregroundRank', 'as_chart_predefined_foregroundRank_name'),
          buildChartOptions(page.charts, 'web-as-foregroundTimeline', 'as_chart_foregroundTimeline_name'),
          buildChartOptions(page.charts, 'web-as-visitingDevices', 'web_as_chart_predefined_visitingDevices_name'),
          buildChartOptions(page.charts, 'web-as-visitTimeline', 'web_as_chart_visitTimeline_name'),
          buildChartOptions(page.charts, 'web-as-errorsTimeline', 'webapp_error_label'),
          buildChartOptions(page.charts, 'web-as-clientErrors', 'web_as_clientErrors_predefined_name'),
          buildChartOptions(page.charts, 'web-as-serverErrors', 'web_as_serverErrors_predefined_name'),
          buildChartOptions(page.charts, 'web-as-webViewErrors', 'web_as_webViewErrors_predefined_name'),
          buildChartOptions(page.charts, 'web-as-sslErrors', 'web_as_sslErrors_predefined_name'),
          buildChartOptions(page.charts, 'web-as-appLoadTime', 'web_as_chart_appLoadTime_predefined_name'),
          buildChartOptions(page.charts, 'web-as-appFirstByteTime', 'web_as_chart_appFirstByteTime_predefined_name'),
        ],
      },
    ];

    patchCard(page.charts, 'web-as-deviceStats', card => {
      card.name = 'web_as_chart_deviceStats_name';
      card.iconTooltip = 'web_as_chart_deviceStats_full_name';
    });

    patchCard<DonutChartConfig>(page.charts, 'web-as-manufacturer', chart => {
      chart.iconTooltip = 'as_chartmanufacturer_fullname';
      chart.reportSummary = 'as_chartmanufacturer_reportsummary';
      chart.name = 'webapp_spotlight_chart_manufacturers_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_manufacturer_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_manufacturer_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_manufacturer_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'webapp_spotlight_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<DonutChartConfig>(page.charts, 'web-as-model', chart => {
      chart.iconTooltip = 'as_chartmodel_fullname';
      chart.name = 'webapp_spotlight_chart_models_name';
      chart.reportSummary = 'as_chartmodel_reportsummary';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_model_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_model_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_model_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_model_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_model_dimension',
        noDataLabel: 'no_data_label',
      };

      chart.tag = {
        name: 'webapp_spotlight_chart_models_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-osVersion', chart => {
      chart.iconTooltip = 'as_chartosVersion_fullname';
      chart.reportSummary = 'as_chartosVersion_reportsummary';
      chart.integerOnly = true;
      chart.name = 'webapp_spotlight_chart_OS_versions_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';

      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_osVersion_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_osVersion_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_osVersion_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_osVersion_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];

      chart.tag = {
        name: 'webapp_spotlight_chart_OS_versions_name',
        parameters: ['topN', 'mostLeast'],
      };
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-foregroundRank', chart => {
      chart.name = 'web_as_chart_foregroundRank_name';
      chart.iconTooltip = 'as_chart_foregroundRank_fullname';
      chart.reportSummary = 'as_chart_foregroundRank_reportsummary';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'web_as_chart_foregroundRank_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'metric',
        label: 'foreground_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("web_as_chart_DeviceName_label", this.point.label)}`',
        labelArgs: ['web_as_chart_DeviceName_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("web_as_chart_foregroundrank_tooltip_value", this.point.yDisplay, this.point.totalTimeDisplay, this.point.devcount)}`',
        valueArgs: [
          'web_as_chart_foregroundrank_tooltip_value',
          'this.point.yDisplay',
          'this.point.totalTimeDisplay',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'web-as-foregroundTimeline', chart => {
      chart.iconTooltip = 'web_as_chart_foregroundTimeline_fullname';
      chart.name = 'web_as_chart_foregroundTimeline_name';
      chart.type = ChartTypeEnum.TIMELINE;
      chart.timelineColors = [
        {
          label: 'as_foreground_usage_total_usage',
          color: ColorConstants.sotiColors('purple', 70),
        },
      ];
      chart.metric = {
        metricId: 'allY',
        label: 'sdd_app_usage_foreground_metric_label',
      };
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'label',
        type: 'mapped',
      };
      chart.tooltip = {
        label: 'as_foreground_usage_total_tooltip_label',
        value: 'web_as_foregroundTimeline_tooltip_value',
        dateTime: 'as_foreground_usage_tooltip_date_time',
      };
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-visitingDevices', chart => {
      chart.iconTooltip = 'web_as_chart_visitingDevices_fullname';
      chart.reportSummary = 'web_as_chart_visitingDevices_reportsummary';
      chart.integerOnly = true;
      chart.name = 'web_as_chart_visitingDevices_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'web_as_chart_visitingDevices_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_chart_visits_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("web_as_chart_DeviceName_label", this.point.label)}`',
        labelArgs: ['web_as_chart_DeviceName_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("web_as_chart_visitingDevices_tooltip_value", this.point.y, this.point.totalVisits, this.point.contribCount)}`',
        valueArgs: [
          'web_as_chart_visitingDevices_tooltip_value',
          'this.point.y',
          'this.point.totalVisits',
          'this.point.contribCount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'web-as-visitTimeline', chart => {
      chart.name = 'web_as_chart_visitTimeline_name';
      chart.integerOnly = true;
      chart.iconTooltip = 'web_as_chart_visitTimeline_fullname';
      chart.type = ChartTypeEnum.TIMELINE;
      chart.timelineColors = [
        {
          label: 'webapp_chart_visits_label',
          color: ColorConstants.sotiColors('yellow', 90),
        },
      ];
      chart.metric = {
        metricId: 'allY',
        label: 'webapp_chart_visits_label',
      };
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };
      chart.tooltip = {
        label: 'web_as_visitTimeline_tooltip_label',
        value: 'web_as_visitTimeline_tooltip_value',
        dateTime: 'as_foreground_usage_tooltip_date_time',
      };
    });

    patchCard<TimelineChartConfig>(page.charts, 'web-as-errorsTimeline', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'web_as_chart_errorsTimeline_fullname';
      chart.layout = '50p';
      chart.name = 'webapp_chart_error_codes_name';

      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };

      chart.timelineColors = [
        {
          label: 'client_error_label',
          color: ColorConstants.sotiColors('yellow', 90),
        },
        {
          label: 'server_error_label',
          color: ColorConstants.sotiColors('cerise', 5),
        },
        {
          label: 'webview_error_label',
          color: ColorConstants.sotiColors('blue', 70),
        },
        {
          label: 'ssl_error_label',
          color: ColorConstants.sotiColors('green', 60),
        },
      ];
      chart.multiTimelineMetric = [
        { metricId: 'clientErrorY', label: 'webapp_client_errors' },
        { metricId: 'serverErrorY', label: 'webapp_server_errors' },
        { metricId: 'webViewErrorY', label: 'webapp_webView_errors' },
        { metricId: 'sslErrorY', label: 'webapp_ssl_errors' },
      ];
      chart.metric = {
        metricId: '',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: 'webapp_error_timeline_tooltip_label',
        value: 'web_as_errorsTimeline_tooltip_value',
        dateTime: 'sdd_data_usage_total_tooltip_date_time',
      };
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-clientErrors', chart => {
      chart.iconTooltip = 'web_as_clientErrors_fullname';
      chart.integerOnly = true;
      chart.name = 'webapp_topClientError';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'error_code_label',
        noDataLabel: 'no_data_label',
      };
      chart.scrollableX = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'webapp_topClientError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'Metric',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("web_as_topErrors_tooltip_value", this.point.y, this.point.devCount)}`',
        valueArgs: ['web_as_topErrors_tooltip_value', 'this.point.y', 'this.point.devCount'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-serverErrors', chart => {
      chart.iconTooltip = 'web_as_serverErrors_fullname';
      chart.integerOnly = true;
      chart.name = 'webapp_topServerError';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'error_code_label',
        noDataLabel: 'no_data_label',
      };
      chart.scrollableX = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'webapp_topServerError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'Metric',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("web_as_topErrors_tooltip_value", this.point.y, this.point.devCount)}`',
        valueArgs: ['web_as_topErrors_tooltip_value', 'this.point.y', 'this.point.devCount'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-webViewErrors', chart => {
      chart.iconTooltip = 'web_as_webViewErrors_fullname';
      chart.integerOnly = true;
      chart.name = 'webapp_topWebViewError';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'error_code_label',
        noDataLabel: 'no_data_label',
      };
      chart.scrollableX = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'webapp_topWebViewError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'Metric',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("web_as_topErrors_tooltip_value", this.point.y, this.point.devCount)}`',
        valueArgs: ['web_as_topErrors_tooltip_value', 'this.point.y', 'this.point.devCount'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-sslErrors', chart => {
      chart.iconTooltip = 'web_as_sslErrors_fullname';
      chart.integerOnly = true;
      chart.name = 'webapp_topSSLError';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'error_code_label',
        noDataLabel: 'no_data_label',
      };
      chart.scrollableX = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'webapp_topSSLError',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'Metric',
        label: 'number_of_errors_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_usage_error_code_label", xsI18nPipe.transform(this.point.label))}`',
        labelArgs: ['webapp_usage_error_code_label', 'xsI18nPipe.transform(this.point.label)'],
        value: '`${xsI18nPipe.transform("web_as_topErrors_tooltip_value", this.point.y, this.point.devCount)}`',
        valueArgs: ['web_as_topErrors_tooltip_value', 'this.point.y', 'this.point.devCount'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-appLoadTime', chart => {
      chart.iconTooltip = 'web_as_chart_appLoadTime_fullname';
      chart.reportSummary = 'web_as_chart_appLoadTime_reportsummary';
      chart.name = 'web_as_chart_appLoadTime_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("web_as_chart_DeviceName_label", this.point.label)}`',
        labelArgs: ['web_as_chart_DeviceName_label', 'this.point.label'],
        value: '`${xsI18nPipe.transform("web_as_chart_avg_time_to_load_value", this.point.y, this.point.devcount)}`',
        valueArgs: ['web_as_chart_avg_time_to_load_value', 'this.point.y', 'this.point.devcount'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.metric = {
        metricId: 'y',
        label: 'webapp_time_label',
      };
      chart.tag = {
        name: 'web_as_chart_appLoadTime_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'web-as-appFirstByteTime', chart => {
      chart.iconTooltip = 'web_as_appFirstByteTime_fullname';
      chart.reportSummary = 'web_as_appFirstByteTime_reportsummary';
      chart.name = 'web_as_chart_appFirstByteTime_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("web_as_chart_DeviceName_label", this.point.label)}`',
        labelArgs: ['web_as_chart_DeviceName_label', 'this.point.label'],
        value: '`${xsI18nPipe.transform("web_as_chart_first_byte_value", this.point.y, this.point.devcount)}`',
        valueArgs: ['web_as_chart_first_byte_value', 'this.point.y', 'this.point.devcount'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.metric = {
        metricId: 'y',
        label: 'webapp_time_label',
      };
      chart.tag = {
        name: 'web_as_chart_appFirstByteTime_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'web-as-deviceTable', (chart: TableConfig) => {
      chart.name = 'web_as_chart_deviceTable_name';
      chart.elementType = 'table';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
      ];
      chart.filters = {
        sortAttribute: 'McDeviceName',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'Column_Name_Device_Name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
            eventParams: {
              nested: true,
              targetAppBaselineId: { ANDROID: DEVICE_APP },
              targetPageBaselineId: { ANDROID: DEVICE_LIST },
            },
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'DeviceGroup',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'GroupPath',
          label: 'group_path_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'OSVersionAsPaddedString',
        },
        {
          id: 'visits',
          label: 'totalWebAppVisits_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'clientErrors',
          label: 'client_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'serverErrors',
          label: 'server_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'webViewErrors',
          label: 'webview_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'sslErrors',
          label: 'ssl_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  const pageIds = [findPageId(app.pages, WEB_APP_SPOTLIGHT_LIST_PAGE)];

  const childPageIds = [findPageId(app.pages, WEB_APP_SPOTLIGHT_INFO_PAGE)];

  const defaultTabId = findPageId(app.pages, WEB_APP_SPOTLIGHT_INFO_PAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}
